import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { createUuid } from '~/_shared/utils/createUuid';
import { RangeType } from '~/_shared/utils/range/range.helpers';
import {
  type BoundaryFillSettingsGroupProps,
  BoundaryFillSettingsLayout,
  type BoundaryFillSettingsNumericProps,
} from '~/sidebar/sidebarApps/mapTools/boundary/customizeFills/boundaryFillSettingsContentComponent';
import { useBoundaryGroupDemographics } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryGroupDemographics';
import { boundaryGroupsChangeName } from '~/store/boundaryGroups/boundaryGroups.actionCreators';
import {
  useBoundaryGroupPermissions, useBoundaryGroupSelector,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { getBoundaryTerritories } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.repository';
import { useBoundaryTerritoryDetailsSelector } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { requestTerritoriesRefresh } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import { useGetBoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  type BoundaryTerritoryGroup, type BoundaryTerritoryGroupStyle,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { usePrimarySpreadsheetFilterTreeItem } from '~/store/spreadsheetData/filtering/usePrimarySpreadsheetFilterTreeItemRequest';
import { BOUNDARY_SETTINGS_MIN_RANGES_COUNT } from './boundarySettings.common';
import {
  BOUNDARY_MAX_VALUE_ID, BOUNDARY_MIN_VALUE_ID, getBoundaryTerritoryRegularGroupsCount,
} from './boundarySettings.helpers';
import { restoreBTGDefaultColors } from './defaultBoundaryTerritoryStyles';
import { useFetchBoundaryTerritories } from './useFetchBoundaryTerritories';

export type BoundaryTerritoryGroupFillPropsVariants = BoundaryFillSettingsNumericProps | BoundaryFillSettingsGroupProps;

export type BoundarySettingsPropsVariantsArguments = {
  boundaryTerritoryGroup?: BoundaryTerritoryGroup;
  onBoundaryTerritoryGroupUpdate: (territoryGroup: BoundaryTerritoryGroup) => void;
  onIsLoadingChange: (isLoading: boolean) => void;
};

export const DEFAULT_RANGES_COUNT = 4;

export const useBoundaryFillSettingsPropsVariants = ({
  boundaryTerritoryGroup,
  onBoundaryTerritoryGroupUpdate,
  onIsLoadingChange,
}: BoundarySettingsPropsVariantsArguments
): BoundaryTerritoryGroupFillPropsVariants | null => {
  const clientId = useClientIdSelector();
  const territoryDetails = useBoundaryTerritoryDetailsSelector();
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();
  const originalBoundaryTerritoryGroup = getBoundaryTerritoryGroupById(boundaryTerritoryGroup?.boundaryTerritoryGroupId);
  const { getDemographic } = useBoundaryGroupDemographics();
  const primarySpreadsheetFilterTreeItem = usePrimarySpreadsheetFilterTreeItem();
  const dispatch = useDispatch();
  const boundaryGroup = useBoundaryGroupSelector(boundaryTerritoryGroup?.boundaryGroupId);
  const boundaryPermissions = useBoundaryGroupPermissions(boundaryTerritoryGroup?.boundaryGroupId);
  const numericalDataForActiveFill = territoryDetails.get(boundaryTerritoryGroup?.boundaryTerritoryGroupId ?? 0)?.numericalData;

  const fetchBoundaryTerritories = useFetchBoundaryTerritories(onBoundaryTerritoryGroupUpdate, onIsLoadingChange);

  return useMemo<BoundaryTerritoryGroupFillPropsVariants | null>(() => {
    if (!boundaryTerritoryGroup || !originalBoundaryTerritoryGroup) {
      return null;
    }

    const settings = boundaryTerritoryGroup.settings;

    //#region Range fill
    //////////////////////////////////////
    // BoundaryTerritoryType.Numeric
    // BoundaryTerritoryType.Density
    // BoundaryTerritoryType.Demographic
    //////////////////////////////////////
    if (
      settings.boundaryTerritoryType === BoundaryTerritoryType.Numeric ||
        settings.boundaryTerritoryType === BoundaryTerritoryType.Density ||
        settings.boundaryTerritoryType === BoundaryTerritoryType.Demographic
    ) {
      const min = numericalDataForActiveFill?.totalLowest ?? BOUNDARY_MIN_VALUE_ID;
      const max = numericalDataForActiveFill?.totalHighest ?? BOUNDARY_MAX_VALUE_ID;

      const demographic = settings.boundaryTerritoryType === BoundaryTerritoryType.Demographic && settings.demographicId
        ? getDemographic(boundaryTerritoryGroup.boundaryGroupId, settings.demographicId)
        : null;

      const disableRangeTypeChange = demographic?.percentage ?? false;

      return {
        min,
        max,
        boundaryGroupName: boundaryGroup?.name ?? '',
        layout: BoundaryFillSettingsLayout.Numeric,
        disableRangeTypeChange,
        isDecimal: !!boundaryTerritoryGroup.settings.boundaryTerritories.find(bt => !bt.custom),
        onNumberOfRangesChange: (numberOfRanges: number, style: BoundaryTerritoryGroupStyle, rangeType: RangeType) => {
          const newBoundaryTerritoryGroup: BoundaryTerritoryGroup = {
            ...boundaryTerritoryGroup,
            settings: {
              ...boundaryTerritoryGroup.settings,
              bucketType: rangeType === RangeType.Value ? 'value-range' : 'percentage-range',
              style,
              rangesCount: numberOfRanges,
            },
          };

          fetchBoundaryTerritories({
            newBoundaryTerritoryGroup,
            originalBoundaryTerritoryGroup,
            filter: primarySpreadsheetFilterTreeItem ? { filter_tree: primarySpreadsheetFilterTreeItem.filterTree } : null,
          });
        },
        onRecalculateRangesClick: () => {
          const newBoundaryTerritoryGroup: BoundaryTerritoryGroup = {
            ...boundaryTerritoryGroup,
            settings: {
              ...boundaryTerritoryGroup.settings,
              bucketType: 'value-range',
            },
          };

          fetchBoundaryTerritories({
            newBoundaryTerritoryGroup,
            originalBoundaryTerritoryGroup,
            filter: primarySpreadsheetFilterTreeItem ? { filter_tree: primarySpreadsheetFilterTreeItem.filterTree } : null,
          });
        },
      };
    }

    //#endregion

    //#region Group fill
    //////////////////////////////////////
    // BoundaryTerritoryType.Groups
    // BoundaryTerritoryType.Manual
    //////////////////////////////////////

    if (
      settings.boundaryTerritoryType === BoundaryTerritoryType.Groups ||
        settings.boundaryTerritoryType === BoundaryTerritoryType.Manual
    ) {
      return {
        layout: BoundaryFillSettingsLayout.Group,
        boundaryGroupName: boundaryGroup?.name ?? '',
        onBoundaryGroupNameChange: (!boundaryGroup || !boundaryPermissions.boundaryIsEditable) ? undefined : (newName) => {
          dispatch(boundaryGroupsChangeName(boundaryGroup.id, newName));
        },
        onRestoreDefaultColors: () => {
          if (settings.boundaryTerritoryType === BoundaryTerritoryType.Manual) {
            onBoundaryTerritoryGroupUpdate(restoreBTGDefaultColors(boundaryTerritoryGroup));
            return;
          }

          // If BTG is of type Group, refetch all groups with their default colors from backend.
          if (!clientId) {
            return;
          }

          const queryId = createUuid();
          const newBoundaryTerritoryGroup: BoundaryTerritoryGroup = restoreBTGDefaultColors({
            ...boundaryTerritoryGroup,
            settings: {
              ...boundaryTerritoryGroup.settings,
              bucketType: 'value',
              boundaryTerritories: requestTerritoriesRefresh(
                boundaryTerritoryGroup.settings.boundaryTerritories,
                originalBoundaryTerritoryGroup.settings.boundaryTerritories
              ),
            },
          });

          onIsLoadingChange(true);
          getBoundaryTerritories(clientId, [{
            boundaryTerritoryGroup: newBoundaryTerritoryGroup,
            boundaryTerritoryGroupQueryId: queryId,
            filter: primarySpreadsheetFilterTreeItem ? {
              filter_tree: primarySpreadsheetFilterTreeItem.filterTree,
            } : null,
          }])
            .then(response => {
              const newTerritoryDetails = response.get(queryId);
              if (!newTerritoryDetails) {
                return;
              }

              onIsLoadingChange(false);
              onBoundaryTerritoryGroupUpdate({
                ...newBoundaryTerritoryGroup,
                settings: {
                  ...newBoundaryTerritoryGroup.settings,
                  // ensure the ranges count to be at least 2 to have a successful API call
                  rangesCount: Math.max(getBoundaryTerritoryRegularGroupsCount(newTerritoryDetails.boundaryTerritories), BOUNDARY_SETTINGS_MIN_RANGES_COUNT),
                  boundaryTerritories: newTerritoryDetails.boundaryTerritories,
                },
              });
            })
            .finally(() => {
              onIsLoadingChange(false);
            });
        },
      };
    }

    //#endregion

    throw new Error(`Invalid boundary territory type: ${settings.boundaryTerritoryType}`);

  },
  [boundaryGroup, boundaryPermissions.boundaryIsEditable, boundaryTerritoryGroup, onIsLoadingChange, clientId,
    dispatch, fetchBoundaryTerritories, getDemographic, numericalDataForActiveFill?.totalHighest,
    numericalDataForActiveFill?.totalLowest, onBoundaryTerritoryGroupUpdate, originalBoundaryTerritoryGroup, primarySpreadsheetFilterTreeItem]);
};
